import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Test, Question, TestAssignment } from '../../types/test';
import Modal from '../../components/Modal';
import './TakeTest.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TakeAssignedTest: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  
  const [assignment, setAssignment] = useState<TestAssignment | null>(null);
  const [test, setTest] = useState<Test | null>(null);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  useEffect(() => {
    if (assignmentId && !authLoading) {
      fetchAssignment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, authLoading]);

  const fetchAssignment = async () => {
    try {
      const response = await fetch(`${API_URL}/assignments/${assignmentId}/take`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setAssignment(data.assignment);
        setTest(data.test);
        
        // Initialize answers
        const initialAnswers: Record<string, string | string[]> = {};
        data.test.questions.forEach((q: Question) => {
          initialAnswers[q.id] = '';
        });
        setAnswers(initialAnswers);
      } else {
        const errorData = await response.json();
        setModal({
          isOpen: true,
          title: 'Cannot Take Test',
          message: errorData.detail || 'This test is no longer available.',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Error fetching assignment:', error);
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to load the test. Please try again.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: string, value: string | string[]) => {
    setAnswers({
      ...answers,
      [questionId]: value,
    });
  };

  const handleNext = () => {
    if (test && currentQuestion < test.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmitClick = () => {
    if (!test || !user) return;
    setShowConfirmModal(true);
  };

  const handleSubmitConfirm = async () => {
    if (!test || !user || !assignment) return;
    
    setShowConfirmModal(false);
    setSubmitting(true);

    try {
      // Format answers for submission
      const formattedAnswers: Record<string, string> = {};
      Object.entries(answers).forEach(([questionId, answer]) => {
        formattedAnswers[questionId] = Array.isArray(answer) ? answer.join(', ') : answer;
      });

      const response = await fetch(`${API_URL}/tests/${test.id}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          answers: formattedAnswers,
          employeeId: user.id,
          employeeName: user.name || user.email,
          employeeEmail: user.email,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data.result);
        setSubmitted(true);
      } else {
        const errorData = await response.json();
        setModal({
          isOpen: true,
          title: 'Submission Failed',
          message: errorData.detail || 'Failed to submit test. Please try again.',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to submit test. Please try again.',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="take-test loading-state">
        <div className="loading-spinner">Loading test...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="take-test">
        <Modal
          isOpen={true}
          onClose={() => navigate('/login')}
          title="Login Required"
          message="Please log in to take this test."
          type="info"
        />
      </div>
    );
  }

  if (submitted && result) {
    return (
      <div className="take-test">
        <div className="test-result">
          <div className="result-header">
            <h1>Test Completed!</h1>
            <p>Thank you for completing the test.</p>
          </div>
          
          <div className="result-card">
            <div className="score-display">
              <div className="score-circle">
                <span className="score-value">{result.percentage?.toFixed(0) || 0}%</span>
              </div>
              <div className="score-details">
                <p className="score-text">
                  You scored <strong>{result.score}</strong> out of <strong>{result.totalPoints}</strong> points
                </p>
              </div>
            </div>
          </div>

          <button 
            className="button-primary"
            onClick={() => navigate('/employee/dashboard')}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="take-test">
        <Modal
          isOpen={modal.isOpen}
          onClose={() => navigate('/employee/dashboard')}
          title={modal.title}
          message={modal.message}
          type={modal.type}
        />
        <div className="error-state">
          <h2>Test Not Available</h2>
          <p>This test could not be loaded.</p>
          <button onClick={() => navigate('/employee/dashboard')}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const question = test.questions[currentQuestion];
  const isLastQuestion = currentQuestion === test.questions.length - 1;
  const answeredCount = Object.values(answers).filter(a => a && a.length > 0).length;

  return (
    <div className="take-test">
      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />

      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Submit Test"
        message={`Are you sure you want to submit? You have answered ${answeredCount} of ${test.questions.length} questions.`}
        type="warning"
        showCancel={true}
        confirmText="Submit"
        onConfirm={handleSubmitConfirm}
      />

      <div className="test-header">
        <h1>{test.title}</h1>
        {assignment?.dueDate && (
          <p className="due-date">
            Due: {new Date(assignment.dueDate).toLocaleString()}
          </p>
        )}
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ width: `${((currentQuestion + 1) / test.questions.length) * 100}%` }}
          />
        </div>
        <p className="progress-text">
          Question {currentQuestion + 1} of {test.questions.length}
        </p>
      </div>

      <div className="question-container">
        <div className="question-card">
          <div className="question-header">
            <span className="question-number">Question {currentQuestion + 1}</span>
            <span className="question-points">{question.points} points</span>
          </div>
          
          <p className="question-text">{question.questionText}</p>

          {question.type === 'multiple-choice' && question.options && (
            <div className="options-list">
              {question.options.map((option, index) => (
                <label key={index} className="option-item">
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={option}
                    checked={answers[question.id] === option}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  />
                  <span className="option-text">{option}</span>
                </label>
              ))}
            </div>
          )}

          {question.type === 'true-false' && (
            <div className="options-list true-false">
              {['True', 'False'].map((option) => (
                <label key={option} className="option-item">
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={option}
                    checked={answers[question.id] === option}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  />
                  <span className="option-text">{option}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="navigation-buttons">
        <button
          className="button-secondary"
          onClick={handlePrevious}
          disabled={currentQuestion === 0}
        >
          ← Previous
        </button>
        
        {!isLastQuestion ? (
          <button
            className="button-primary"
            onClick={handleNext}
          >
            Next →
          </button>
        ) : (
          <button
            className="button-submit"
            onClick={handleSubmitClick}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Test'}
          </button>
        )}
      </div>
    </div>
  );
};

export default TakeAssignedTest;
